import {
  CheckCircle,
  LoaderCircle,
  Pencil,
  Printer,
  Plus,
  RotateCcw,
  ShoppingCart,
  Trash2,
  Truck,
  X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import type { EnrichedOrder, OrderSummary } from "@/features/order/types";

export type OrderCardActionsProps = {
  order: EnrichedOrder | OrderSummary;
  variant?: "row" | "detail";
  isSuper: boolean;
  canManageOrders?: boolean;
  canDeleteOrder?: boolean;
  isClient: boolean;
  canComplete: boolean;
  isStale: boolean;
  isPastDate: boolean;
  canOpenCheckin: boolean;
  highlighted?: boolean;
  isPending: boolean;
  isExporting?: boolean;
  canEditChangeRequest?: boolean;
  isDeleting: boolean;
  confirmDelete: boolean;
  showAddItem: boolean;
  onComplete: () => void;
  onNavigateToQuotation: () => void;
  onOpenChangeRequest: () => void;
  onReopen: () => void;
  onToggleAddItem: () => void;
  onOpenCheckin: () => void;
  onPrintOrder?: () => void;
  onRequestDelete: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
};

export function hasOrderCardActions({
  order,
  isSuper,
  canManageOrders = isSuper,
  canDeleteOrder,
  isClient,
  canComplete,
  isStale,
  isPastDate,
  canOpenCheckin,
}: Pick<
  OrderCardActionsProps,
  | "order"
  | "isSuper"
  | "canManageOrders"
  | "canDeleteOrder"
  | "isClient"
  | "canComplete"
  | "isStale"
  | "isPastDate"
  | "canOpenCheckin"
>) {
  if (isStale && !canManageOrders) return false;

  const canDelete =
    !(isClient && order.status === "change_requested") &&
    (canDeleteOrder ??
      (isSuper ||
        (!canManageOrders &&
          order.status !== "encerrado" &&
          order.status !== "carregamento")));
  return (
    (canComplete && order.status === "open") ||
    (!canManageOrders && order.status === "open") ||
    (isClient && order.status === "change_requested") ||
    (isClient &&
      (order.status === "concluido" || order.status === "carregamento")) ||
    (canManageOrders && order.status === "encerrado") ||
    (canManageOrders && order.status === "open") ||
    canOpenCheckin ||
    canDelete
  );
}

export function hasOrderCardRowActions({
  order,
  isSuper,
  isClient,
  canManageOrders,
  canComplete,
  canOpenCheckin,
}: Pick<
  OrderCardActionsProps,
  | "order"
  | "isSuper"
  | "isClient"
  | "canManageOrders"
  | "canComplete"
  | "canOpenCheckin"
>) {
  return (
    ((!canManageOrders || canComplete) && order.status === "open") ||
    (isClient &&
      ["concluido", "carregamento", "change_requested"].includes(
        order.status,
      )) ||
    (canOpenCheckin && canManageOrders) ||
    (isSuper &&
      ["concluido", "carregamento", "encerrado"].includes(order.status))
  );
}

export function OrderCardActions({
  order,
  variant = "detail",
  isSuper,
  canManageOrders = isSuper,
  canDeleteOrder,
  isClient,
  canComplete,
  isStale,
  isPastDate,
  canOpenCheckin,
  highlighted,
  isPending,
  isExporting = false,
  canEditChangeRequest = true,
  isDeleting,
  confirmDelete,
  showAddItem,
  onComplete,
  onNavigateToQuotation,
  onOpenChangeRequest,
  onReopen,
  onToggleAddItem,
  onOpenCheckin,
  onPrintOrder,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
}: OrderCardActionsProps) {
  const canDelete =
    !(isClient && order.status === "change_requested") &&
    (canDeleteOrder ??
      (isSuper ||
        (!canManageOrders &&
          order.status !== "encerrado" &&
          order.status !== "carregamento")));
  const canPrintOrder =
    isSuper &&
    onPrintOrder &&
    ["concluido", "carregamento", "encerrado"].includes(order.status);
  const isCompactSuperOperationalSet =
    isSuper &&
    !isClient &&
    canManageOrders &&
    canOpenCheckin &&
    canDelete &&
    !(canComplete && order.status === "open") &&
    order.status !== "encerrado" &&
    order.status !== "cancelled";
  const isThreeActionSet =
    !isClient &&
    canManageOrders &&
    canDelete &&
    order.status !== "encerrado" &&
    order.status !== "cancelled" &&
    ((canComplete && order.status === "open" && !canOpenCheckin) ||
      (canOpenCheckin && !(canComplete && order.status === "open"))) &&
    !canPrintOrder;
  const checkinLabel = isCompactSuperOperationalSet
    ? "Acompanhar"
    : "Acompanhar carga";
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmDeleteButtonRef = useRef<HTMLButtonElement>(null);
  const previousConfirmDeleteRef = useRef(confirmDelete);

  useEffect(() => {
    const wasConfirmingDelete = previousConfirmDeleteRef.current;
    previousConfirmDeleteRef.current = confirmDelete;

    if (!wasConfirmingDelete && confirmDelete) {
      confirmDeleteButtonRef.current?.focus();
    } else if (wasConfirmingDelete && !confirmDelete) {
      deleteTriggerRef.current?.focus();
    }
  }, [confirmDelete]);

  if (
    !hasOrderCardActions({
      order,
      isSuper,
      canManageOrders,
      canDeleteOrder,
      isClient,
      canComplete,
      isStale,
      isPastDate,
      canOpenCheckin,
    })
  ) {
    return null;
  }

  if (variant === "row") {
    if (
      !hasOrderCardRowActions({
        order,
        isSuper,
        isClient,
        canManageOrders,
        canComplete,
        canOpenCheckin,
      })
    )
      return null;
    return (
      <div className="order-card-actions" data-testid="order-card-row-actions">
        {!canManageOrders && order.status === "open" && (
          <button
            className="button compact blue"
            type="button"
            onClick={onNavigateToQuotation}
            disabled={isPending}
          >
            <ShoppingCart size={14} />
            <span>Editar pedido</span>
          </button>
        )}
        {canComplete && order.status === "open" && (
          <button
            className="button compact primary"
            data-testid={`complete-order-${order.id}`}
            type="button"
            onClick={onComplete}
            disabled={isPending}
          >
            <CheckCircle size={14} />
            <span>Concluir</span>
          </button>
        )}
        {isClient &&
          (order.status === "concluido" ||
            order.status === "carregamento" ||
            order.status === "change_requested") && (
            <button
              className="button compact blue"
              type="button"
              onClick={onOpenChangeRequest}
              disabled={
                isPending ||
                (order.status === "change_requested" && !canEditChangeRequest)
              }
            >
              <Pencil size={14} />
              <span>
                {order.status === "change_requested"
                  ? "Editar solicitação"
                  : "Solicitar alteração"}
              </span>
            </button>
          )}
        {canOpenCheckin && canManageOrders && (
          <button
            className="button compact balanced-action"
            type="button"
            onClick={onOpenCheckin}
            disabled={isPending}
          >
            <Truck size={14} />
            <span>Acompanhar</span>
          </button>
        )}
        {canPrintOrder && (
          <button
            className="button compact"
            type="button"
            onClick={onPrintOrder}
            aria-busy={isExporting}
            disabled={isPending || isExporting}
          >
            {isExporting ? (
              <LoaderCircle className="order-action-spinner" size={14} />
            ) : (
              <Printer size={14} />
            )}
            <span>Imprimir pedido</span>
          </button>
        )}
      </div>
    );
  }

  if (confirmDelete && canDelete) {
    return (
      <div
        className="order-card-actions"
        data-testid="order-card-actions"
        data-confirming="true"
      >
        <div className="order-delete-confirmation">
          <span>Tem certeza?</span>
          <button
            ref={confirmDeleteButtonRef}
            className="button danger"
            type="button"
            onClick={onConfirmDelete}
            disabled={isDeleting || isPending}
          >
            {isDeleting ? (
              <LoaderCircle className="order-action-spinner" size={14} />
            ) : (
              <Trash2 size={14} />
            )}
            <span>{isDeleting ? "Excluindo…" : "Confirmar exclusão"}</span>
          </button>
          <button
            className="button"
            type="button"
            onClick={onCancelDelete}
            disabled={isDeleting}
          >
            <X size={14} />
            <span>Cancelar</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`order-card-actions${isThreeActionSet ? " order-card-actions--three" : ""}`}
      data-testid="order-card-actions"
    >
      {canDelete && (
        <button
          ref={deleteTriggerRef}
          className="button danger"
          type="button"
          onClick={onRequestDelete}
          disabled={isPending}
        >
          <Trash2 size={14} />
          <span>Excluir</span>
        </button>
      )}
      {!canManageOrders && order.status === "open" && (
        <button
          className="button blue"
          type="button"
          onClick={onNavigateToQuotation}
          disabled={isPending}
        >
          <ShoppingCart size={14} />
          <span>Editar pedido</span>
        </button>
      )}
      {isClient &&
        (order.status === "concluido" || order.status === "carregamento") && (
          <button
            className="button blue"
            type="button"
            onClick={onOpenChangeRequest}
            disabled={isPending}
          >
            <Pencil size={14} />
            <span>Solicitar alteração</span>
          </button>
        )}
      {isClient && order.status === "change_requested" && (
        <button
          className="button"
          type="button"
          onClick={onOpenChangeRequest}
          disabled={isPending || !canEditChangeRequest}
        >
          <Pencil size={14} />
          <span>Editar solicitação</span>
        </button>
      )}
      {canManageOrders &&
        (order.status === "encerrado" || order.status === "cancelled") && (
          <button
            className="button"
            type="button"
            onClick={onReopen}
            disabled={isPending}
          >
            <RotateCcw size={14} />
            <span>Reabrir</span>
          </button>
        )}
      {canManageOrders && (
        <button
          className="button"
          data-active={showAddItem ? "true" : undefined}
          type="button"
          onClick={onToggleAddItem}
          disabled={isPending}
        >
          <Plus size={14} />
          <span>Adicionar item</span>
        </button>
      )}
      {canOpenCheckin && (
        <button
          className={`button${isThreeActionSet ? " primary" : ""}`}
          type="button"
          onClick={onOpenCheckin}
          disabled={isPending}
        >
          <Truck size={14} />
          <span>{checkinLabel}</span>
        </button>
      )}
      {canPrintOrder && (
        <button
          className="button"
          type="button"
          onClick={onPrintOrder}
          aria-busy={isExporting}
          disabled={isPending || isExporting}
        >
          {isExporting ? (
            <LoaderCircle className="order-action-spinner" size={14} />
          ) : (
            <Printer size={14} />
          )}
          <span>Imprimir pedido</span>
        </button>
      )}
      {canComplete && order.status === "open" && (
        <button
          className="button primary"
          data-testid={`complete-order-${order.id}`}
          data-highlighted={highlighted ? "true" : undefined}
          type="button"
          onClick={onComplete}
          disabled={isPending}
        >
          <CheckCircle size={14} />
          <span>Concluir</span>
        </button>
      )}
    </div>
  );
}
